<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <div class="titolo-card__info">
            <h1>Gestione consulenze</h1>
            <p class="titolo-card__subtitle">
              Controllo unificato delle consulenze automatiche e manuali.
            </p>
          </div>
          <div class="titolo-card__actions">
            <button
              v-if="canRunBackfill"
              type="button"
              class="create-user-button staff-button-neutral"
              :disabled="backfillRunning"
              @click="runBackfill"
            >
              {{ backfillRunning ? 'Aggiorno indice…' : 'Aggiorna indice' }}
            </button>
            <span class="badge-counter">
              {{ isAgendaDataset ? `${totalDocsCount} consulenze` : `${totalDocsCount} percorsi` }}
            </span>
            <span v-if="!isAgendaDataset" class="badge-counter badge-counter--secondary">
              {{ consultationStatusTotals.total }} consulenze
            </span>
            <span v-if="!isAgendaDataset" class="threshold-badge">In scadenza entro {{ EXPIRING_THRESHOLD_DAYS }} giorni</span>
          </div>
        </div>

        <div v-if="backfillStatus || backfillError" class="backfill-feedback">
          <span v-if="backfillStatus" class="muted">{{ backfillStatus }}</span>
          <span v-if="backfillError" class="error">{{ backfillError }}</span>
        </div>

        <div class="dataset-switch" role="tablist" aria-label="Tipo consulenze">
          <button
            type="button"
            class="dataset-switch__button"
            :class="{ 'dataset-switch__button--active': selectedDataset === 'automatiche' }"
            @click="selectedDataset = 'automatiche'"
          >
            Automatiche
          </button>
          <button
            type="button"
            class="dataset-switch__button"
            :class="{ 'dataset-switch__button--active': selectedDataset === 'manuali' }"
            @click="selectedDataset = 'manuali'"
          >
            Manuali
          </button>
          <button
            type="button"
            class="dataset-switch__button"
            :class="{ 'dataset-switch__button--active': selectedDataset === 'agenda' }"
            @click="selectedDataset = 'agenda'"
          >
            Agenda
          </button>
        </div>

        <section class="page-help page-help--consulenze">
          <div class="page-help__header">
            <span class="page-help__title">Come leggere gli stati</span>
            <span class="page-help__subtitle">
              {{ isAgendaDataset ? 'Significato dello stato della singola consulenza' : 'Scadenza del percorso e stati delle singole consulenze' }}
            </span>
          </div>

          <div v-if="!isAgendaDataset" class="page-help__items">
            <div class="page-help__item">
              <strong>Attiva</strong>
              <span>Il percorso e ancora valido e mancano piu di 30 giorni alla scadenza.</span>
            </div>
            <div class="page-help__item">
              <strong>In scadenza</strong>
              <span>Il percorso e ancora valido ma mancano 30 giorni o meno alla scadenza.</span>
            </div>
            <div class="page-help__item">
              <strong>Scaduta</strong>
              <span>La data limite del percorso e gia passata.</span>
            </div>
            <div class="page-help__item">
              <strong>Completata</strong>
              <span>Tutte le consulenze del percorso risultano gia chiuse.</span>
            </div>
          </div>

          <div v-else class="page-help__items">
            <div class="page-help__item">
              <strong>Da fare</strong>
              <span>La consulenza non e ancora stata gestita.</span>
            </div>
            <div class="page-help__item">
              <strong>Contattata</strong>
              <span>L'utente e stato contattato, ma la consulenza non e ancora chiusa.</span>
            </div>
            <div class="page-help__item">
              <strong>Prenotata</strong>
              <span>L'appuntamento e stato fissato.</span>
            </div>
            <div class="page-help__item">
              <strong>Rifiutata</strong>
              <span>La consulenza non verra svolta.</span>
            </div>
            <div class="page-help__item">
              <strong>Fatta</strong>
              <span>La consulenza e stata conclusa.</span>
            </div>
            <div class="page-help__item">
              <strong>Scalata</strong>
              <span>La consulenza e stata spostata.</span>
            </div>
          </div>

          <p v-if="!isAgendaDataset" class="page-help__note">
            Per le automatiche la scadenza coincide con la consegna del programma piu 30 giorni.
            Se la consegna e gia stata effettuata si usa la data reale, altrimenti quella prevista.
          </p>
          <p v-else class="page-help__note">
            In Agenda si guarda sempre la singola consulenza.
          </p>
        </section>

        <div class="status-cards" v-if="!isInitialLoading && !isAgendaDataset">
          <div class="status-card">
            <span class="status-card__label">Percorsi totali</span>
            <strong>{{ statusCounts.total }}</strong>
          </div>
          <div class="status-card status-card--active">
            <span class="status-card__label">Percorsi attivi</span>
            <strong>{{ statusCounts.active }}</strong>
          </div>
          <div class="status-card status-card--expiring">
            <span class="status-card__label">Percorsi in scadenza</span>
            <strong>{{ statusCounts.expiring }}</strong>
          </div>
          <div class="status-card status-card--expired">
            <span class="status-card__label">Percorsi scaduti</span>
            <strong>{{ statusCounts.expired }}</strong>
          </div>
          <div class="status-card status-card--completed">
            <span class="status-card__label">Percorsi completati</span>
            <strong>{{ statusCounts.completed }}</strong>
          </div>
        </div>

        <div class="consultation-status-cards" v-if="!isInitialLoading && !isAgendaDataset">
          <div
            v-for="status in CONSULENZA_STATUS_OPTIONS"
            :key="`summary-${status}`"
            class="consultation-status-card"
            :class="`consultation-status-card--${CONSULENZA_STATUS_META[status]?.tone || 'daFare'}`"
          >
            <span class="status-card__label">{{ status }}</span>
            <strong>{{ getConsulenzaStatusCountFromCounts(consultationStatusTotals, status) }}</strong>
          </div>
        </div>

        <div v-if="!isAgendaDataset" class="filters">
          <div class="filter-group">
            <label for="studioFilter">Studio</label>
            <select id="studioFilter" v-model="selectedStudio">
              <option value="">Tutti</option>
              <option
                v-for="studio in availableStudios"
                :key="studio.value"
                :value="studio.value"
              >
                {{ studio.label }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="categoriaFilter">Categoria</label>
            <select id="categoriaFilter" v-model="selectedCategoria">
              <option value="">Tutte</option>
              <option
                v-for="categoria in availableCategorie"
                :key="categoria"
                :value="categoria"
              >
                {{ categoria }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="numeroConsulenzeFilter">Numero consulenze</label>
            <select id="numeroConsulenzeFilter" v-model="selectedNumeroConsulenze">
              <option value="">Tutte</option>
              <option
                v-for="numero in availableNumeroConsulenze"
                :key="`tot-${numero}`"
                :value="numero"
              >
                {{ numero }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="packageStateFilter">Stato percorso</label>
            <select id="packageStateFilter" v-model="selectedPackageState">
              <option value="">Tutti</option>
              <option
                v-for="state in availablePackageStates"
                :key="state.value"
                :value="state.value"
              >
                {{ state.label }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="consulenzaStateFilter">Stato consulenza</label>
            <select id="consulenzaStateFilter" v-model="selectedConsulenzaState">
              <option value="">Tutti</option>
              <option
                v-for="state in availableConsulenzaStates"
                :key="state.value"
                :value="state.value"
              >
                {{ state.label }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="operationalHoldFilter">Utenti sospesi</label>
            <select id="operationalHoldFilter" v-model="selectedOperationalHold">
              <option value="exclude">Nascondi sospesi</option>
              <option value="">Tutti</option>
              <option value="only">Solo sospesi</option>
            </select>
          </div>
        </div>

        <div v-else class="filters filters--agenda">
          <div class="filter-group">
            <label for="studioFilterAgenda">Studio</label>
            <select id="studioFilterAgenda" v-model="selectedStudio">
              <option value="">Tutti</option>
              <option
                v-for="studio in availableStudios"
                :key="studio.value"
                :value="studio.value"
              >
                {{ studio.label }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="categoriaFilterAgenda">Categoria</label>
            <select id="categoriaFilterAgenda" v-model="selectedCategoria">
              <option value="">Tutte</option>
              <option
                v-for="categoria in availableCategorie"
                :key="categoria"
                :value="categoria"
              >
                {{ categoria }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="agendaDateFrom">Da</label>
            <input id="agendaDateFrom" v-model="agendaDateFrom" type="date">
          </div>

          <div class="filter-group">
            <label for="agendaDateTo">A</label>
            <input id="agendaDateTo" v-model="agendaDateTo" type="date">
          </div>

          <div class="filter-group">
            <label for="agendaConsulenzaStateFilter">Stato consulenza</label>
            <select id="agendaConsulenzaStateFilter" v-model="selectedAgendaConsulenzaState">
              <option value="">Tutti</option>
              <option
                v-for="status in availableAgendaConsulenzaStates"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label for="operationalHoldFilterAgenda">Utenti sospesi</label>
            <select id="operationalHoldFilterAgenda" v-model="selectedOperationalHold">
              <option value="exclude">Nascondi sospesi</option>
              <option value="">Tutti</option>
              <option value="only">Solo sospesi</option>
            </select>
          </div>
        </div>

        <div class="consulenze-summary" v-if="!isInitialLoading && (!isAgendaDataset || agendaHasRange)">
          <span v-if="isAgendaDataset">
            Mostrando {{ visibleDocsCount }} di {{ totalDocsCount }} consulenze pianificate
          </span>
          <span v-else>
            Mostrando {{ visibleUserGroupsCount }} utenti, {{ totalDocsCount }} percorsi e {{ consultationStatusTotals.total }} consulenze
          </span>
          <span v-if="!isFullDataReady" class="loading-indicator">(risultati parziali, carica altri)</span>
        </div>

        <div v-if="isInitialLoading && !isAgendaDataset" class="loading-shell" aria-hidden="true">
          <div class="loading-shell__header">
            <div class="skeleton skeleton--chip"></div>
            <div class="skeleton skeleton--chip skeleton--chip-wide"></div>
          </div>
          <div class="loading-shell__cards">
            <div v-for="index in 5" :key="`card-${index}`" class="loading-card">
              <div class="skeleton skeleton--label"></div>
              <div class="skeleton skeleton--value"></div>
            </div>
          </div>
          <div class="loading-shell__table">
            <div class="loading-row loading-row--head">
              <div v-for="index in 10" :key="`head-${index}`" class="skeleton skeleton--cell"></div>
            </div>
            <div v-for="row in 4" :key="`row-${row}`" class="loading-row">
              <div v-for="index in 10" :key="`cell-${row}-${index}`" class="skeleton skeleton--cell"></div>
            </div>
          </div>
        </div>

        <div v-else-if="isInitialLoading && isAgendaDataset" class="loading-shell loading-shell--agenda" aria-hidden="true">
          <div class="loading-shell__header">
            <div class="skeleton skeleton--chip"></div>
            <div class="skeleton skeleton--chip skeleton--chip-wide"></div>
          </div>
          <div class="loading-shell__table">
            <div class="loading-row loading-row--head">
              <div v-for="index in 8" :key="`agenda-head-${index}`" class="skeleton skeleton--cell"></div>
            </div>
            <div v-for="row in 5" :key="`agenda-row-${row}`" class="loading-row">
              <div v-for="index in 8" :key="`agenda-cell-${row}-${index}`" class="skeleton skeleton--cell"></div>
            </div>
          </div>
        </div>

        <div v-else-if="isAgendaDataset && !agendaHasRange" class="agenda-empty-state">
          Imposta un intervallo `Da / A` per caricare le consulenze automatiche pianificate.
        </div>

        <div v-else-if="!isAgendaDataset" class="user-groups">
          <div
            v-for="group in visibleUserGroups"
            :key="group.groupKey"
            class="user-group"
          >
            <button
              type="button"
              class="user-group__header"
              @click="toggleUserGroup(group.groupKey)"
            >
              <div class="user-group__identity">
                <strong>{{ group.email || '—' }}</strong>
                <div class="user-group__meta">
                  <span>{{ group.studio || 'N/D' }}</span>
                  <span v-if="group.uid" class="mono">{{ group.uid }}</span>
                  <span v-if="group.hasOperationalHold" class="group-stat group-stat--expired">Sospeso</span>
                </div>
              </div>

              <div class="user-group__stats">
                <span class="group-stat">{{ group.packageCount }} percorsi</span>
                <span class="group-stat group-stat--consultations">{{ group.totalConsultations }} consulenze</span>
                <span
                  v-for="item in consultationStatusBreakdown(group.consulenzaStatusCounts)"
                  :key="`${group.groupKey}-${item.value}`"
                  class="group-stat"
                  :class="`group-stat--consulenza-${item.tone}`"
                >
                  {{ item.label }} {{ item.count }}
                </span>
                <span v-if="group.counts.completed" class="group-stat group-stat--completed">
                  {{ group.counts.completed }} completati
                </span>
                <span v-if="group.counts.expiring" class="group-stat group-stat--expiring">
                  {{ group.counts.expiring }} in scadenza
                </span>
                <span v-if="group.counts.expired" class="group-stat group-stat--expired">
                  {{ group.counts.expired }} scaduti
                </span>
                <span v-if="group.counts.active" class="group-stat group-stat--active">
                  {{ group.counts.active }} attivi
                </span>
              </div>

              <span class="material-icons user-group__chevron" aria-hidden="true">
                {{ isUserGroupOpen(group.groupKey) ? 'expand_less' : 'expand_more' }}
              </span>
            </button>

            <div v-if="isUserGroupOpen(group.groupKey)" class="table-wrap table-wrap--nested">
              <table class="consulenze-table consulenze-table--nested">
                <thead>
                  <tr>
                    <th class="order-cell"></th>
                    <th>Origine</th>
                    <th>Percorso</th>
                    <th>Categoria</th>
                    <th>Consulenze</th>
                    <th>Stato consulenze</th>
                    <th>Scadenza</th>
                    <th>Stato percorso</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="doc in group.rows"
                    :key="doc.rowKey"
                    :class="rowClass(doc)"
                  >
                    <td class="order-cell">
                      <button
                        type="button"
                        class="order-badge"
                        :class="`order-badge--${orderBadgeStatus(doc)}`"
                        @click="openOrderModal(doc)"
                        :disabled="!doc.orderLookupEmail || (orderLoading && activeOrderDocId === doc.rowKey)"
                        title="Mostra ultimo ordine"
                        aria-label="Mostra ultimo ordine"
                      >
                        <span class="material-icons" aria-hidden="true">receipt_long</span>
                      </button>
                    </td>

                    <td>
                      <span class="origin-pill" :class="`origin-pill--${doc.sourceKind}`">
                        {{ sourceLabel(doc.sourceKind) }}
                      </span>
                    </td>

                    <td>
                      <div class="package-cell">
                        <strong>{{ doc.packageTitle }}</strong>
                        <small class="muted">{{ doc.packageSubtitle }}</small>
                      </div>
                    </td>

                    <td>{{ doc.categoryLabel }}</td>

                    <td>
                      <div class="count-cell">
                        <strong>{{ doc.totalConsultations }} totali</strong>
                        <small class="muted">{{ doc.doneConsultations }} chiuse</small>
                      </div>
                    </td>

                    <td>
                      <div class="consultation-breakdown">
                        <span
                          v-for="item in consultationStatusBreakdown(doc.consulenzaStatusCounts)"
                          :key="`${doc.rowKey}-${item.value}`"
                          class="consultation-status-pill"
                          :class="`consultation-status-pill--${item.tone}`"
                        >
                          {{ item.label }} · {{ item.count }}
                        </span>
                        <small v-if="!consultationStatusBreakdown(doc.consulenzaStatusCounts).length" class="muted">
                          Nessuno stato disponibile
                        </small>
                      </div>
                    </td>

                    <td>
                      <div class="expiry-cell">
                        <strong>{{ formatDateValue(doc.expireAt) || '—' }}</strong>
                        <small class="muted">{{ expiryHelperLabel(doc) }}</small>
                      </div>
                    </td>

                    <td>
                      <span class="state-pill" :class="`state-pill--${packageStatus(doc)}`">
                        {{ stateLabel(packageStatus(doc)) }}
                      </span>
                    </td>

                    <td class="action-cell">
                      <div class="row-actions">
                        <button
                          type="button"
                          class="expire-toggle-button"
                          :class="{ 'expire-toggle-button--active': isExpiredPackage(doc) }"
                          :title="isExpiredPackage(doc) ? 'Rimuovi scaduto' : 'Segna scaduto'"
                          :disabled="isExpiryToggleBusy(doc) || isCompletedPackage(doc)"
                          @click="toggleExpired(doc)"
                        >
                          <span class="material-icons" aria-hidden="true">
                            {{ isExpiredPackage(doc) ? 'remove_circle' : 'schedule' }}
                          </span>
                          {{ isExpiredPackage(doc) ? 'Rimuovi scaduto' : 'Segna scaduto' }}
                        </button>

                        <button
                          type="button"
                          v-if="doc.uid"
                          @click="openUserProgrammiInNewTab(doc.uid)"
                          class="go-to-user-button"
                          title="Apri programmi utente"
                        >
                          <span class="material-icons">visibility</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="!visibleUserGroups.length" class="empty-state">
            Nessuna consulenza corrisponde ai filtri selezionati.
          </div>
        </div>

        <div v-else class="table-wrap">
          <table class="consulenze-table consulenze-table--agenda">
            <thead>
              <tr>
                <th class="order-cell"></th>
                <th>Email utente</th>
                <th>Studio</th>
                <th>Programma</th>
                <th>Categoria</th>
                <th>Data prevista</th>
                <th>Scadenza</th>
                <th>Status</th>
                <th>Professionista</th>
                <th>Note</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody v-if="visibleDocs.length">
              <tr
                v-for="doc in visibleDocs"
                :key="doc.rowKey"
              >
                <td class="order-cell">
                  <button
                    type="button"
                    class="order-badge"
                    :class="`order-badge--${orderBadgeStatus(doc)}`"
                    @click="openOrderModal(doc)"
                    :disabled="!doc.orderLookupEmail || (orderLoading && activeOrderDocId === doc.rowKey)"
                    title="Mostra ultimo ordine"
                    aria-label="Mostra ultimo ordine"
                  >
                    <span class="material-icons" aria-hidden="true">receipt_long</span>
                  </button>
                </td>
                <td>
                  <div class="user-cell">
                    <strong>{{ doc.email || '—' }}</strong>
                    <small v-if="doc.uid" class="muted mono">{{ doc.uid }}</small>
                    <small v-if="isOperationallyHidden(doc)" class="muted">Sospeso</small>
                  </div>
                </td>
                <td>{{ doc.studio || 'N/D' }}</td>
                <td>
                  <div class="package-cell">
                    <strong>{{ doc.packageTitle }}</strong>
                    <small class="muted">{{ doc.packageSubtitle || '—' }}</small>
                  </div>
                </td>
                <td>{{ doc.categoryLabel }}</td>
                <td>
                  <div class="expiry-cell">
                    <strong>{{ formatDateValue(doc.plannedAt) || '—' }}</strong>
                  </div>
                </td>
                <td>
                  <div class="expiry-cell">
                    <strong>{{ formatDateValue(doc.expireAt) || '—' }}</strong>
                  </div>
                </td>
                <td>
                  <div class="agenda-status-cell">
                    <select
                      class="agenda-status-select"
                      :value="normalizeConsulenzaStatus(doc.consulenzaStatus || 'Da fare')"
                      :disabled="isAgendaStatusBusy(doc) || !canEditAgendaStatus(doc)"
                      @change="updateAgendaConsulenzaStatus(doc, $event.target.value)"
                    >
                      <option
                        v-for="status in CONSULENZA_STATUS_OPTIONS"
                        :key="status"
                        :value="status"
                      >
                        {{ status }}
                      </option>
                    </select>
                  </div>
                </td>
                <td>{{ doc.trainerName || '—' }}</td>
                <td>
                  <div class="agenda-note-cell">
                    <textarea
                      class="agenda-note-input"
                      rows="2"
                      :value="doc.staffNote || ''"
                      placeholder="Note interne"
                      :disabled="isAgendaNoteBusy(doc) || !canEditAgendaNote(doc)"
                      @change="updateAgendaConsulenzaNote(doc, $event.target.value)"
                    />
                    <small v-if="isAgendaNoteBusy(doc)" class="muted">salvo...</small>
                  </div>
                </td>
                <td class="action-cell">
                  <div class="row-actions">
                    <button
                      type="button"
                      v-if="doc.uid"
                      @click="openUserConsulenzeInNewTab(doc.uid)"
                      class="go-to-user-button"
                      title="Apri dettaglio utente"
                    >
                      <span class="material-icons">visibility</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="10" class="empty-state">
                  Nessuna consulenza automatica pianificata nel periodo selezionato.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="hasMoreResults && !isInitialLoading" class="consulenze-pagination">
          <button type="button" class="create-user-button staff-button-neutral" :disabled="isLoadingMore" @click="loadMoreResults">
            {{ isLoadingMore ? 'Caricamento…' : (isAgendaDataset ? 'Mostra altre consulenze' : 'Mostra altri pacchetti') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showOrderModal" class="modal-backdrop" @click.self="closeOrderModal">
    <div class="modal order-modal">
      <div class="modal-header">
        <h3 class="modal-title">Ultimo ordine</h3>
        <button type="button" class="modal-close" @click="closeOrderModal" aria-label="Chiudi">
          ×
        </button>
      </div>
      <div v-if="orderLoading" class="modal-state">Caricamento…</div>
      <div v-else-if="orderError" class="modal-state error">{{ orderError }}</div>
      <div v-else-if="orderSummary" class="order-card">
        <div class="order-card__body">
          <div class="order-field">
            <span class="order-field__label">Email</span>
            <span class="order-field__value">{{ activeOrderEmail }}</span>
          </div>
          <div class="order-field">
            <span class="order-field__label">Codice</span>
            <span class="order-field__value">{{ orderSummary.code }}</span>
          </div>
          <div class="order-field">
            <span class="order-field__label">Data</span>
            <span class="order-field__value">{{ orderSummary.date }}</span>
          </div>
          <div class="order-field">
            <span class="order-field__label">Totale</span>
            <span class="order-field__value">{{ orderSummary.total }}</span>
          </div>
          <div class="order-field">
            <span class="order-field__label">Stato</span>
            <span class="order-field__value">{{ orderSummary.status }}</span>
          </div>
        </div>
        <div v-if="orderSummary.products.length" class="order-card__products">
          <h4>Prodotti</h4>
          <ul class="order-products">
            <li
              v-for="product in orderSummary.products"
              :key="product"
              :class="productHighlightClass(product)"
            >
              {{ product }}
            </li>
          </ul>
        </div>
      </div>
      <div v-else class="modal-state muted">Nessun ordine trovato.</div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import SidebarComponent from '../SidebarComponent.vue';
import {
  collection,
  doc as firestoreDoc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ORDER_BADGE_KEYWORDS, keywordMatches } from '@/lib/orderBadgeKeywords';
import { shouldHideFromOperationalLists } from '@/lib/operationalHold';

const DAY_MS = 24 * 60 * 60 * 1000;
const EXPIRING_THRESHOLD_DAYS = 30;
const EMPTY_STUDIO_VALUE = '__none__';
const PACKAGES_COLLECTION = 'consulenze_packages_index';
const AGENDA_COLLECTION = 'consulenze_agenda_index';
const BACKFILL_BATCH_SIZE = 120;
const BACKFILL_MAX_LOOPS = 400;
const BACKFILL_ALLOWED_UID = 'HpMiFyBZnwX6EUf6VR9iCsHkqo82';
const DEFAULT_NUMERO_CONSULENZE_OPTIONS = Object.freeze(
  Array.from({ length: 10 }, (_, index) => index + 1)
);

const SOURCE_META = Object.freeze({
  automatiche: { label: 'Automatiche' },
  manuali: { label: 'Manuali' },
  agenda: { label: 'Agenda automatiche' },
});

const STATE_META = Object.freeze({
  active: { label: 'Attiva', priority: 2 },
  expiring: { label: 'In scadenza', priority: 1 },
  expired: { label: 'Scaduta', priority: 0 },
  completed: { label: 'Completata', priority: 3 },
});
const PACKAGE_STATE_OPTIONS = Object.freeze(['active', 'expiring', 'expired', 'completed']);

const CONSULENZA_STATUS_OPTIONS = Object.freeze(['Da fare', 'Contattata', 'Prenotata', 'Rifiutata', 'Fatta', 'Scalata']);
const CONSULENZA_STATUS_COUNT_KEYS = Object.freeze({
  'Da fare': 'daFare',
  Contattata: 'contattata',
  Prenotata: 'prenotata',
  Rifiutata: 'rifiutata',
  Fatta: 'fatta',
  Scalata: 'scalata',
});
const CONSULENZA_STATUS_META = Object.freeze({
  'Da fare': { label: 'Da fare', tone: 'daFare' },
  Contattata: { label: 'Contattata', tone: 'contattata' },
  Prenotata: { label: 'Prenotata', tone: 'prenotata' },
  Rifiutata: { label: 'Rifiutata', tone: 'rifiutata' },
  Fatta: { label: 'Fatta', tone: 'fatta' },
  Scalata: { label: 'Scalata', tone: 'scalata' },
});
const VALID_CONSULENZA = new Set(CONSULENZA_STATUS_OPTIONS);

const createEmptyConsulenzaStatusCounts = () => ({
  daFare: 0,
  contattata: 0,
  prenotata: 0,
  rifiutata: 0,
  fatta: 0,
  scalata: 0,
});

const normalizeConsulenzaStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'contattata' || normalized === 'contattato') return 'Contattata';
  if (normalized === 'prenotata' || normalized === 'prenotato') return 'Prenotata';
  if (normalized === 'rifiutata' || normalized === 'rifiutato') return 'Rifiutata';
  if (normalized === 'fatta' || normalized === 'fatto') return 'Fatta';
  if (normalized === 'scalata' || normalized === 'scalato') return 'Scalata';
  return 'Da fare';
};

const isConsulenzaStatusDone = (value) => {
  const normalized = normalizeConsulenzaStatus(value);
  return normalized === 'Fatta' || normalized === 'Rifiutata' || normalized === 'Scalata';
};

const isAgendaVisibleConsulenzaStatus = (value) => {
  const normalized = normalizeConsulenzaStatus(value);
  return VALID_CONSULENZA.has(normalized);
};

const normalizeConsulenzaStatusCounts = (value) => {
  const counts = createEmptyConsulenzaStatusCounts();
  if (!value || typeof value !== 'object') return counts;

  Object.values(CONSULENZA_STATUS_COUNT_KEYS).forEach((key) => {
    const amount = Number(value[key]);
    counts[key] = Number.isFinite(amount) && amount > 0 ? amount : 0;
  });

  return counts;
};

const getConsulenzaStatusCount = (docItem, status) => {
  const normalized = normalizeConsulenzaStatus(status);
  const key = CONSULENZA_STATUS_COUNT_KEYS[normalized];
  if (!key) return 0;
  return Number(docItem?.consulenzaStatusCounts?.[key]) || 0;
};

const getConsulenzaStatusCountFromCounts = (counts, status) => {
  const normalized = normalizeConsulenzaStatus(status);
  const key = CONSULENZA_STATUS_COUNT_KEYS[normalized];
  if (!key) return 0;
  return Number(counts?.[key]) || 0;
};

const mergeConsulenzaStatusCounts = (base, extra) => {
  const source = normalizeConsulenzaStatusCounts(extra);
  return {
    daFare: Number(base?.daFare || 0) + Number(source.daFare || 0),
    contattata: Number(base?.contattata || 0) + Number(source.contattata || 0),
    prenotata: Number(base?.prenotata || 0) + Number(source.prenotata || 0),
    rifiutata: Number(base?.rifiutata || 0) + Number(source.rifiutata || 0),
    fatta: Number(base?.fatta || 0) + Number(source.fatta || 0),
    scalata: Number(base?.scalata || 0) + Number(source.scalata || 0),
  };
};

const consultationStatusBreakdown = (counts) => (
  CONSULENZA_STATUS_OPTIONS
    .map((status) => ({
      value: status,
      label: CONSULENZA_STATUS_META[status]?.label || status,
      tone: CONSULENZA_STATUS_META[status]?.tone || 'daFare',
      count: getConsulenzaStatusCountFromCounts(counts, status),
    }))
    .filter((item) => item.count > 0)
);

const normalizeStepsArray = (steps) => {
  if (Array.isArray(steps)) {
    return steps.map((step) => (step && typeof step === 'object' ? { ...step } : step));
  }

  if (steps && typeof steps === 'object') {
    return Object.keys(steps)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => {
        const step = steps[key];
        return step && typeof step === 'object' ? { ...step } : step;
      });
  }

  return [];
};

export default {
  name: 'ConsulenzeBatchList',
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const router = useRouter();
    const functions = getFunctions(undefined, 'us-central1');
    const auth = getAuth();

    const initialPackages = ref([]);
    const automaticheCache = ref(null);
    const manualiCache = ref(null);
    const agendaCache = ref({ key: '', rows: null });
    const isInitialLoading = ref(true);
    const isFullDataReady = ref(false);
    const isLoadingMore = ref(false);

    const selectedDataset = ref('automatiche');
    const selectedStudio = ref('');
    const selectedCategoria = ref('');
    const selectedNumeroConsulenze = ref('');
    const selectedPackageState = ref('');
    const selectedConsulenzaState = ref('');
    const selectedAgendaConsulenzaState = ref('');
    const selectedOperationalHold = ref('exclude');
    const agendaDateFrom = ref('');
    const agendaDateTo = ref('');

    const orderHighlights = reactive({});
    const orderLoading = ref(false);
    const orderError = ref('');
    const showOrderModal = ref(false);
    const selectedOrder = ref(null);
    const activeOrderDocId = ref(null);
    const activeOrderEmail = ref('');
    const expireToggleLoading = reactive({});
    const operatorUid = ref(auth.currentUser?.uid || '');
    const backfillRunning = ref(false);
    const backfillStatus = ref('');
    const backfillError = ref('');
    const agendaStatusSaving = reactive({});
    const agendaNoteSaving = reactive({});
    const openUserGroups = reactive({});
    let activeLoadToken = 0;
    let authUnsub = null;

    const startOfDay = (date) => {
      const next = new Date(date);
      next.setHours(0, 0, 0, 0);
      return next;
    };
    const endOfDay = (date) => {
      const next = new Date(date);
      next.setHours(23, 59, 59, 999);
      return next;
    };

    const toDateSafe = (value) => {
      if (value === undefined || value === null || value === '') return null;
      if (typeof value?.toDate === 'function') {
        try {
          const converted = value.toDate();
          return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
        } catch {
          return null;
        }
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDateValue = (value) => {
      const date = toDateSafe(value);
      return date ? date.toLocaleDateString('it-IT') : '';
    };

    const parseOrderDate = (order) => {
      const candidates = [order?.orderDate, order?.importedAt, order?.updatedAt, order?.createdAt];
      for (const value of candidates) {
        const parsed = toDateSafe(value);
        if (parsed) return parsed;
      }
      return null;
    };

    const formatOrderDate = (order) => {
      const date = parseOrderDate(order);
      if (!date) return 'Data n/d';
      return date.toLocaleDateString('it-IT', { dateStyle: 'medium' });
    };

    const formatOrderPrice = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      return num.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };

    const fetchLatestOrderByEmail = async (email) => {
      const trimmedEmail = String(email || '').trim();
      if (!trimmedEmail) return null;

      const ordersRef = collection(db, 'orders');
      const snap = await getDocs(query(ordersRef, where('userEmail', '==', trimmedEmail)));
      if (snap.empty) return null;

      return snap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => {
          const bTime = parseOrderDate(b)?.getTime() ?? 0;
          const aTime = parseOrderDate(a)?.getTime() ?? 0;
          return bTime - aTime;
        })[0] || null;
    };

    const categorizeOrder = (order) => {
      if (!order) return 'neutral';
      const productsArray = Array.isArray(order.products) ? order.products : [];
      const names = productsArray
        .map((item) => item?.name || item?.title || item?.productName || item?.sku)
        .filter(Boolean)
        .map((name) => String(name).toLowerCase());

      if (keywordMatches(names, ORDER_BADGE_KEYWORDS.green)) return 'green';
      if (keywordMatches(names, ORDER_BADGE_KEYWORDS.orange)) return 'orange';
      return 'neutral';
    };

    const formatStatus = (status) => {
      if (!status) return '—';
      return String(status)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const productHighlightClass = (productName = '') => {
      const normalized = String(productName).toLowerCase();
      if (keywordMatches([normalized], ORDER_BADGE_KEYWORDS.green)) {
        return 'order-product order-product--green';
      }
      if (keywordMatches([normalized], ORDER_BADGE_KEYWORDS.orange)) {
        return 'order-product order-product--orange';
      }
      return 'order-product order-product--neutral';
    };

    const setOrderHighlight = (docItem, status) => {
      const keys = [];
      if (docItem?.uid) keys.push(docItem.uid);
      const emailKey = docItem?.email ? String(docItem.email).trim() : '';
      if (emailKey) keys.push(emailKey);

      keys.forEach((key) => {
        if (!key) return;
        if (status && status !== 'neutral') {
          orderHighlights[key] = { status };
        } else if (orderHighlights[key]) {
          delete orderHighlights[key];
        }
      });
    };

    const orderBadgeStatus = (docItem) => {
      const keys = [];
      if (docItem?.uid) keys.push(docItem.uid);
      const emailKey = docItem?.email ? String(docItem.email).trim() : '';
      if (emailKey) keys.push(emailKey);
      return keys.map((key) => orderHighlights[key]?.status).find(Boolean) || 'neutral';
    };

    const mapPackageIndexDoc = (docSnap) => {
      const data = docSnap.data() || {};
      return {
        rowKey: docSnap.id,
        id: data.sourceDocId || docSnap.id,
        collectionName: data.sourceCollection || 'percorsi',
        sourceKind: data.sourceKind || 'automatiche',
        purchaseKey: data.purchaseKey || '',
        sequenceGroupId: data.sequenceGroupId || '',
        sequenceIndex: Number(data.sequenceIndex) || null,
        sequenceTotal: Number(data.sequenceTotal) || null,
        uid: data.uid || '',
        email: data.userEmail || '',
        studio: data.userStudio || '',
        packageTitle: data.packageTitle || 'Pacchetto consulenze',
        packageSubtitle: data.packageSubtitle || '',
        categoryLabel: data.categoryLabel || 'Da definire',
        totalConsultations: Number(data.totalConsultations) || 0,
        doneConsultations: Number(data.doneConsultations) || 0,
        consulenzaStatusCounts: normalizeConsulenzaStatusCounts(data.consulenzaStatusCounts),
        createdAt: data.createdAt || null,
        expireAt: data.expireAt || null,
        scadutoManuale: data.scadutoManuale ?? null,
        scadutoManualeUpdatedAt: data.scadutoManualeUpdatedAt || null,
        orderLookupEmail: data.orderLookupEmail || data.userEmail || '',
        operationalHold: data.operationalHold || null,
        operationalHoldActive: data.operationalHoldActive === true,
        operationalHoldReason: data.operationalHoldReason || '',
        operationalHoldNote: data.operationalHoldNote || '',
        operationalHoldHideFromOperationalLists: data.operationalHoldHideFromOperationalLists === true,
        operationalHoldSuppressNotifications: data.operationalHoldSuppressNotifications === true,
      };
    };

    const mapAgendaIndexDoc = (docSnap) => {
      const data = docSnap.data() || {};
      const parsedStepIndex = Number.parseInt(data.stepIndex, 10);

      return {
        rowKey: docSnap.id,
        id: docSnap.id,
        sourceKind: 'agenda',
        sourceCollection: data.sourceCollection || 'percorsi',
        sourceDocId: data.sourceDocId || data.percorsoId || '',
        percorsoId: data.percorsoId || '',
        stepIndex: Number.isInteger(parsedStepIndex) ? parsedStepIndex : null,
        uid: data.uid || '',
        email: data.userEmail || '',
        studio: data.userStudio || '',
        packageTitle: data.packageTitle || 'Programma consulenze',
        packageSubtitle: data.packageSubtitle || '',
        categoryLabel: data.categoryLabel || 'Da definire',
        plannedAt: data.plannedAt || null,
        expireAt: data.expireAt || null,
        consulenzaStatus: data.consulenzaStatus || 'Da fare',
        scalataAt: data.scalataAt || null,
        trainerName: data.trainerName || '',
        staffNote: data.staffNote || '',
        orderLookupEmail: data.orderLookupEmail || data.userEmail || '',
        operationalHold: data.operationalHold || null,
        operationalHoldActive: data.operationalHoldActive === true,
        operationalHoldReason: data.operationalHoldReason || '',
        operationalHoldNote: data.operationalHoldNote || '',
        operationalHoldHideFromOperationalLists: data.operationalHoldHideFromOperationalLists === true,
        operationalHoldSuppressNotifications: data.operationalHoldSuppressNotifications === true,
      };
    };

    const hydrateAgendaRowsFromPercorsi = async (rows = []) => {
      const percorsoIds = Array.from(new Set(
        rows
          .map((row) => row.sourceDocId || row.percorsoId)
          .filter(Boolean)
      ));
      if (!percorsoIds.length) return rows;

      const entries = await Promise.all(percorsoIds.map(async (percorsoId) => {
        try {
          const snap = await getDoc(firestoreDoc(db, 'percorsi', percorsoId));
          return [percorsoId, snap.exists() ? snap.data() : null];
        } catch (error) {
          console.error('Errore rilettura percorso per agenda consulenze:', error);
          return [percorsoId, null];
        }
      }));
      const percorsoDataById = new Map(entries);

      return rows.map((row) => {
        const percorsoId = row.sourceDocId || row.percorsoId;
        const data = percorsoDataById.get(percorsoId);
        const stepIndex = Number.parseInt(row.stepIndex, 10);
        const step = Array.isArray(data?.steps) && Number.isInteger(stepIndex)
          ? data.steps[stepIndex]
          : null;
        if (!step || typeof step !== 'object') return row;

        return {
          ...row,
          consulenzaStatus: step.consulenzaStatus || row.consulenzaStatus || 'Da fare',
          scalataAt: step.scalataAt ?? row.scalataAt ?? null,
          trainerName: step.TrainerNome || row.trainerName || '',
          staffNote: step.staffNote || '',
        };
      });
    };

    const currentAgendaKey = computed(() => (
      agendaHasRange.value ? `${agendaDateFrom.value}|${agendaDateTo.value}` : ''
    ));

    const loadAutomaticheFullDocs = async (loadToken = activeLoadToken) => {
      if (automaticheCache.value !== null) {
        initialPackages.value = automaticheCache.value;
        isFullDataReady.value = true;
        isInitialLoading.value = false;
        return;
      }

      try {
        const snap = await getDocs(query(
          collection(db, PACKAGES_COLLECTION),
          where('sourceKind', '==', 'automatiche')
        ));
        const rows = snap.docs.map(mapPackageIndexDoc);
        if (loadToken !== activeLoadToken) return;
        automaticheCache.value = rows;
        initialPackages.value = rows;
        isFullDataReady.value = true;
      } catch (error) {
        console.error('Errore caricamento completo consulenze automatiche:', error);
        if (loadToken === activeLoadToken) {
          initialPackages.value = [];
          isFullDataReady.value = true;
        }
      } finally {
        if (loadToken === activeLoadToken) {
          isInitialLoading.value = false;
        }
      }
    };
    const loadManualiFullDocs = async (loadToken = activeLoadToken) => {
      if (manualiCache.value !== null) {
        initialPackages.value = manualiCache.value;
        isFullDataReady.value = true;
        isInitialLoading.value = false;
        return;
      }

      try {
        const snap = await getDocs(query(
          collection(db, PACKAGES_COLLECTION),
          where('sourceKind', '==', 'manuali')
        ));
        const rows = snap.docs.map(mapPackageIndexDoc);
        if (loadToken !== activeLoadToken) return;
        manualiCache.value = rows;
        initialPackages.value = rows;
        isFullDataReady.value = true;
      } catch (error) {
        console.error('Errore caricamento completo consulenze manuali:', error);
        if (loadToken === activeLoadToken) {
          initialPackages.value = [];
          isFullDataReady.value = true;
        }
      } finally {
        if (loadToken === activeLoadToken) {
          isInitialLoading.value = false;
        }
      }
    };
    const loadAgendaFullDocs = async (loadToken = activeLoadToken) => {
      if (
        agendaCache.value.rows !== null &&
        agendaCache.value.key === currentAgendaKey.value
      ) {
        initialPackages.value = agendaCache.value.rows;
        isFullDataReady.value = true;
        isInitialLoading.value = false;
        return;
      }

      try {
        const fromDate = startOfDay(new Date(`${agendaDateFrom.value}T00:00:00`));
        const toDate = endOfDay(new Date(`${agendaDateTo.value}T00:00:00`));
        const snap = await getDocs(query(
          collection(db, AGENDA_COLLECTION),
          where('plannedAt', '>=', fromDate),
          where('plannedAt', '<=', toDate),
          orderBy('plannedAt', 'asc')
        ));
        const rows = await hydrateAgendaRowsFromPercorsi(snap.docs.map(mapAgendaIndexDoc));
        if (loadToken !== activeLoadToken) return;
        agendaCache.value = {
          key: currentAgendaKey.value,
          rows,
        };
        initialPackages.value = rows;
        isFullDataReady.value = true;
      } catch (error) {
        console.error('Errore caricamento completo agenda consulenze:', error);
        if (loadToken === activeLoadToken) {
          initialPackages.value = [];
          isFullDataReady.value = true;
        }
      } finally {
        if (loadToken === activeLoadToken) {
          isInitialLoading.value = false;
        }
      }
    };

    const resetOrderHighlights = () => {
      Object.keys(orderHighlights).forEach((key) => {
        delete orderHighlights[key];
      });
    };

    const clearDatasetCaches = () => {
      automaticheCache.value = null;
      manualiCache.value = null;
      agendaCache.value = { key: '', rows: null };
    };

    const canRunBackfill = computed(() => operatorUid.value === BACKFILL_ALLOWED_UID);

    const reloadDataset = () => {
      activeLoadToken += 1;
      initialPackages.value = [];
      isInitialLoading.value = true;
      isFullDataReady.value = false;
      resetOrderHighlights();

      if (selectedDataset.value === 'agenda' && !agendaHasRange.value) {
        isInitialLoading.value = false;
        isFullDataReady.value = true;
        return;
      }

      if (selectedDataset.value === 'automatiche') {
        loadAutomaticheFullDocs(activeLoadToken);
        return;
      }

      if (selectedDataset.value === 'manuali') {
        loadManualiFullDocs(activeLoadToken);
        return;
      }

      if (selectedDataset.value === 'agenda') {
        loadAgendaFullDocs(activeLoadToken);
        return;
      }

      isInitialLoading.value = false;
      isFullDataReady.value = true;
    };

    onMounted(() => {
      operatorUid.value = auth.currentUser?.uid || '';
      authUnsub = onAuthStateChanged(auth, (user) => {
        operatorUid.value = user?.uid || '';
      });
      reloadDataset();
    });

    onUnmounted(() => {
      if (typeof authUnsub === 'function') {
        authUnsub();
      }
      showOrderModal.value = false;
    });

    const isAgendaDataset = computed(() => selectedDataset.value === 'agenda');
    const agendaHasRange = computed(() => Boolean(agendaDateFrom.value && agendaDateTo.value));
    const docsForFilters = computed(() => (
      selectedDataset.value === 'automatiche' && automaticheCache.value !== null
        ? automaticheCache.value
        : selectedDataset.value === 'manuali' && manualiCache.value !== null
          ? manualiCache.value
          : selectedDataset.value === 'agenda' && agendaCache.value.key === currentAgendaKey.value
            ? (agendaCache.value.rows || [])
            : initialPackages.value
    ));

    const purchaseKeyForDoc = (docItem) => {
      const explicitKey = String(docItem?.purchaseKey || '').trim();
      if (explicitKey) return explicitKey;

      const sequenceGroupId = String(docItem?.sequenceGroupId || '').trim();
      if (sequenceGroupId) return sequenceGroupId;

      const sourceKind = String(docItem?.sourceKind || 'unknown').trim() || 'unknown';
      const sourceCollection = String(docItem?.collectionName || docItem?.sourceCollection || 'unknown').trim() || 'unknown';
      const sourceDocId = String(docItem?.id || docItem?.sourceDocId || docItem?.rowKey || '').trim();
      return `${sourceKind}__${sourceCollection}__${sourceDocId}`;
    };

    const userGroupKeyForDoc = (docItem) => {
      const emailKey = String(docItem?.email || '').trim().toLowerCase();
      const uidKey = String(docItem?.uid || '').trim();
      return uidKey || emailKey || docItem?.rowKey || '';
    };

    const isOperationallyHidden = (docItem) => shouldHideFromOperationalLists(docItem || {});

    const purchaseSummaries = computed(() => {
      const summaries = new Map();
      if (isAgendaDataset.value) return summaries;

      docsForFilters.value.forEach((item) => {
        const purchaseKey = purchaseKeyForDoc(item);
        if (!purchaseKey) return;

        if (!summaries.has(purchaseKey)) {
          summaries.set(purchaseKey, {
            purchaseKey,
            totalConsultations: 0,
          });
        }

        const summary = summaries.get(purchaseKey);
        summary.totalConsultations += Number(item.totalConsultations) || 0;
      });

      return summaries;
    });

    const isCompletedPackage = (docItem) => {
      const total = Number(docItem?.totalConsultations);
      const done = Number(docItem?.doneConsultations);
      if (!Number.isFinite(total) || total <= 0) return false;
      return done >= total;
    };

    const isExpiredPackage = (docItem) => {
      if (!docItem) return false;
      if (isCompletedPackage(docItem)) return false;
      if (docItem.scadutoManuale === false) return false;
      if (docItem.scadutoManuale === true) return true;

      const expireAt = toDateSafe(docItem.expireAt);
      if (!expireAt) return false;
      return Date.now() >= expireAt.getTime();
    };

    const daysUntilExpiry = (docItem) => {
      const expireAt = toDateSafe(docItem?.expireAt);
      if (!expireAt) return null;
      return Math.ceil((expireAt.getTime() - Date.now()) / DAY_MS);
    };

    const isExpiringPackage = (docItem) => {
      if (!docItem || isCompletedPackage(docItem) || isExpiredPackage(docItem)) return false;
      const days = daysUntilExpiry(docItem);
      return days !== null && days >= 0 && days <= EXPIRING_THRESHOLD_DAYS;
    };

    const packageStatus = (docItem) => {
      if (isCompletedPackage(docItem)) return 'completed';
      if (isExpiredPackage(docItem)) return 'expired';
      if (isExpiringPackage(docItem)) return 'expiring';
      return 'active';
    };

    const stateLabel = (state) => STATE_META[state]?.label || 'Attiva';
    const sourceLabel = (source) => SOURCE_META[source]?.label || 'Sconosciuta';
    const openUserConsulenzeInNewTab = (uid) => {
      if (!uid) return;
      const resolved = router.resolve({ name: 'UserConsulenze', params: { userId: uid } });
      window.open(resolved.href, '_blank', 'noopener');
    };
    const openUserProgrammiInNewTab = (uid) => {
      if (!uid) return;
      const resolved = router.resolve({ name: 'UserProgrammi', params: { userId: uid } });
      window.open(resolved.href, '_blank', 'noopener');
    };

    const completionLabel = (docItem) => {
      if (isCompletedPackage(docItem)) return 'Consulenza completata';
      if (!docItem?.doneConsultations) return 'Nessuna consulenza chiusa';
      return `${docItem.doneConsultations} completate`;
    };

    const expiryHelperLabel = (docItem) => {
      if (isCompletedPackage(docItem)) return 'Consulenza completata';
      if (docItem?.scadutoManuale === true) return 'Segnato manualmente come scaduto';
      if (docItem?.scadutoManuale === false) return 'Scadenza ignorata manualmente';

      const days = daysUntilExpiry(docItem);
      if (days === null) return 'Nessuna scadenza disponibile';
      if (days < 0) {
        const abs = Math.abs(days);
        return abs === 1 ? 'Scaduto da 1 giorno' : `Scaduto da ${abs} giorni`;
      }
      if (days === 0) return 'Scade oggi';
      if (days === 1) return 'Scade domani';
      return `Scade tra ${days} giorni`;
    };

    const rowClass = (docItem) => `row--${packageStatus(docItem)}`;

    const patchLocalDoc = (rowKey, patch) => {
      if (!rowKey) return;

      const patchRows = (rows) => rows.map((item) => {
        if (item.rowKey !== rowKey) return item;
        return { ...item, ...patch };
      });

      initialPackages.value = patchRows(initialPackages.value);
      if (automaticheCache.value !== null) {
        automaticheCache.value = patchRows(automaticheCache.value);
      }
      if (manualiCache.value !== null) {
        manualiCache.value = patchRows(manualiCache.value);
      }
      if (agendaCache.value.rows !== null) {
        agendaCache.value = {
          ...agendaCache.value,
          rows: patchRows(agendaCache.value.rows),
        };
      }
    };

    const removeLocalDoc = (rowKey) => {
      if (!rowKey) return;

      const removeRows = (rows) => rows.filter((item) => item.rowKey !== rowKey);

      initialPackages.value = removeRows(initialPackages.value);
      if (automaticheCache.value !== null) {
        automaticheCache.value = removeRows(automaticheCache.value);
      }
      if (manualiCache.value !== null) {
        manualiCache.value = removeRows(manualiCache.value);
      }
      if (agendaCache.value.rows !== null) {
        agendaCache.value = {
          ...agendaCache.value,
          rows: removeRows(agendaCache.value.rows),
        };
      }
    };

    const isExpiryToggleBusy = (docItem) => Boolean(expireToggleLoading[docItem?.rowKey]);

    const getAgendaStepRef = (docItem) => {
      const percorsoId = docItem?.sourceDocId || docItem?.percorsoId || '';
      const sourceCollection = docItem?.sourceCollection || 'percorsi';
      const stepIndex = Number.parseInt(docItem?.stepIndex, 10);

      if (sourceCollection !== 'percorsi') return null;
      if (!percorsoId || !Number.isInteger(stepIndex) || stepIndex < 0) return null;

      return { percorsoId, stepIndex };
    };

    const isAgendaStatusBusy = (docItem) => Boolean(agendaStatusSaving[docItem?.rowKey]);
    const isAgendaNoteBusy = (docItem) => Boolean(agendaNoteSaving[docItem?.rowKey]);
    const canEditAgendaStatus = (docItem) => getAgendaStepRef(docItem) !== null;
    const canEditAgendaNote = (docItem) => getAgendaStepRef(docItem) !== null;
    const updateAgendaIndexDoc = async (rowKey, patch) => {
      if (!rowKey) return;
      await updateDoc(firestoreDoc(db, AGENDA_COLLECTION, rowKey), {
        ...patch,
        updatedAt: new Date(),
      });
    };

    const toggleExpired = async (docItem) => {
      const rowKey = docItem?.rowKey;
      if (!rowKey || isExpiryToggleBusy(docItem)) return;
      if (isCompletedPackage(docItem)) return;

      const nextValue = isExpiredPackage(docItem) ? false : true;
      const updatedAt = new Date().toISOString();
      expireToggleLoading[rowKey] = true;

      try {
        await updateDoc(
          firestoreDoc(db, docItem.collectionName, docItem.id),
          {
            scadutoManuale: nextValue,
            scadutoManualeUpdatedAt: updatedAt,
          }
        );

        patchLocalDoc(rowKey, {
          scadutoManuale: nextValue,
          scadutoManualeUpdatedAt: updatedAt,
        });
      } catch (error) {
        console.error('Errore nel toggle dello stato scaduto:', error);
      } finally {
        delete expireToggleLoading[rowKey];
      }
    };

    const updateAgendaConsulenzaStatus = async (docItem, newStatus) => {
      const rowKey = docItem?.rowKey;
      if (!rowKey || !isAgendaDataset.value) return;

      const normalizedStatus = normalizeConsulenzaStatus(newStatus);
      if (!VALID_CONSULENZA.has(normalizedStatus)) return;
      if (isAgendaStatusBusy(docItem)) return;

      const stepRef = getAgendaStepRef(docItem);
      if (!stepRef) {
        return;
      }
      const { percorsoId, stepIndex } = stepRef;

      agendaStatusSaving[rowKey] = true;

      const nowIso = new Date().toISOString();
      const isDone = isConsulenzaStatusDone(normalizedStatus);
      const nextScalataAt = normalizedStatus === 'Scalata'
        ? (docItem?.scalataAt || nowIso)
        : (docItem?.scalataAt || null);
      const percorsoRef = firestoreDoc(db, 'percorsi', percorsoId);

      try {
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const steps = normalizeStepsArray(data.steps);
        const prevStep = steps[stepIndex];
        if (!prevStep || typeof prevStep !== 'object') return;

        steps[stepIndex] = {
          ...prevStep,
          consulenzaStatus: normalizedStatus,
          done: isDone,
          doneAt: normalizedStatus === 'Fatta' ? (prevStep.doneAt || nowIso) : null,
          scalataAt: normalizedStatus === 'Scalata'
            ? (prevStep.scalataAt || nowIso)
            : (prevStep.scalataAt || null),
        };

        await updateDoc(percorsoRef, { steps });

        if (isAgendaVisibleConsulenzaStatus(normalizedStatus)) {
          await updateAgendaIndexDoc(rowKey, {
            consulenzaStatus: normalizedStatus,
            scalataAt: nextScalataAt,
          });
          patchLocalDoc(rowKey, {
            consulenzaStatus: normalizedStatus,
            scalataAt: nextScalataAt,
          });
        } else {
          removeLocalDoc(rowKey);
        }
      } catch (error) {
        console.error("Errore nell'aggiornamento dello stato consulenza dell'agenda:", error);
      } finally {
        delete agendaStatusSaving[rowKey];
      }
    };

    const updateAgendaConsulenzaNote = async (docItem, value) => {
      const rowKey = docItem?.rowKey;
      if (!rowKey || !isAgendaDataset.value) return;
      if (isAgendaNoteBusy(docItem)) return;

      const stepRef = getAgendaStepRef(docItem);
      if (!stepRef) return;

      const { percorsoId, stepIndex } = stepRef;
      const staffNote = String(value || '').trim();
      const percorsoRef = firestoreDoc(db, 'percorsi', percorsoId);

      agendaNoteSaving[rowKey] = true;

      try {
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const steps = normalizeStepsArray(data.steps);
        const prevStep = steps[stepIndex];
        if (!prevStep || typeof prevStep !== 'object') return;

        steps[stepIndex] = {
          ...prevStep,
          staffNote,
        };

        await updateDoc(percorsoRef, { steps });
        await updateAgendaIndexDoc(rowKey, { staffNote });

        patchLocalDoc(rowKey, { staffNote });
      } catch (error) {
        console.error("Errore nell'aggiornamento delle note consulenza dell'agenda:", error);
      } finally {
        delete agendaNoteSaving[rowKey];
      }
    };

    const scopedDocs = computed(() => {
      let filtered = docsForFilters.value;

      if (selectedOperationalHold.value === 'exclude') {
        filtered = filtered.filter((item) => !isOperationallyHidden(item));
      } else if (selectedOperationalHold.value === 'only') {
        filtered = filtered.filter((item) => isOperationallyHidden(item));
      }

      if (selectedStudio.value) {
        if (selectedStudio.value === EMPTY_STUDIO_VALUE) {
          filtered = filtered.filter((item) => !item.studio);
        } else {
          filtered = filtered.filter((item) => (item.studio || '') === selectedStudio.value);
        }
      }

      if (selectedCategoria.value) {
        filtered = filtered.filter((item) => item.categoryLabel === selectedCategoria.value);
      }

      if (isAgendaDataset.value) {
        const fromDate = agendaDateFrom.value ? startOfDay(new Date(`${agendaDateFrom.value}T00:00:00`)) : null;
        const toDate = agendaDateTo.value ? endOfDay(new Date(`${agendaDateTo.value}T00:00:00`)) : null;
        filtered = filtered.filter((item) => {
          const plannedAt = toDateSafe(item.plannedAt);
          if (!plannedAt) return false;
          if (fromDate && plannedAt < fromDate) return false;
          if (toDate && plannedAt > toDate) return false;
          return true;
        });

        if (selectedAgendaConsulenzaState.value) {
          filtered = filtered.filter((item) => (
            normalizeConsulenzaStatus(item.consulenzaStatus || 'Da fare') === selectedAgendaConsulenzaState.value
          ));
        }
      }

      if (!isAgendaDataset.value && selectedNumeroConsulenze.value !== '') {
        const numero = Number(selectedNumeroConsulenze.value);
        filtered = filtered.filter((item) => {
          const purchaseSummary = purchaseSummaries.value.get(purchaseKeyForDoc(item));
          return Number(purchaseSummary?.totalConsultations || 0) === numero;
        });
      }

      return filtered;
    });

    const nonAgendaStateMatchedDocs = computed(() => {
      if (isAgendaDataset.value) return [];

      let filtered = scopedDocs.value;

      if (selectedPackageState.value) {
        filtered = filtered.filter((item) => packageStatus(item) === selectedPackageState.value);
      }

      if (selectedConsulenzaState.value) {
        filtered = filtered.filter((item) => (
          getConsulenzaStatusCount(item, selectedConsulenzaState.value) > 0
        ));
      }

      return filtered;
    });

    const hasGroupLevelStateFilters = computed(() => (
      !isAgendaDataset.value && Boolean(selectedPackageState.value || selectedConsulenzaState.value)
    ));

    const matchingGroupKeys = computed(() => {
      if (!hasGroupLevelStateFilters.value) return null;

      return new Set(
        nonAgendaStateMatchedDocs.value
          .map((item) => userGroupKeyForDoc(item))
          .filter(Boolean)
      );
    });

    const statusCounts = computed(() => {
      if (isAgendaDataset.value) {
        return {
          total: scopedDocs.value.length,
          active: 0,
          expiring: 0,
          expired: 0,
          completed: 0,
        };
      }
      const counts = {
        total: visibleDocs.value.length,
        active: 0,
        expiring: 0,
        expired: 0,
        completed: 0,
      };

      visibleDocs.value.forEach((item) => {
        const status = packageStatus(item);
        counts[status] += 1;
      });

      return counts;
    });

    const consultationStatusTotals = computed(() => {
      const totals = createEmptyConsulenzaStatusCounts();
      let totalConsultations = 0;

      if (isAgendaDataset.value) {
        scopedDocs.value.forEach((item) => {
          totalConsultations += 1;
          const normalized = normalizeConsulenzaStatus(item.consulenzaStatus);
          const key = CONSULENZA_STATUS_COUNT_KEYS[normalized];
          if (key) totals[key] += 1;
        });

        return {
          total: totalConsultations,
          ...totals,
        };
      }

      visibleDocs.value.forEach((item) => {
        totalConsultations += Number(item.totalConsultations) || 0;
        const merged = mergeConsulenzaStatusCounts(totals, item.consulenzaStatusCounts);
        totals.daFare = merged.daFare;
        totals.contattata = merged.contattata;
        totals.prenotata = merged.prenotata;
        totals.rifiutata = merged.rifiutata;
        totals.fatta = merged.fatta;
        totals.scalata = merged.scalata;
      });

      return {
        total: totalConsultations,
        ...totals,
      };
    });

    const filteredAndSortedDocs = computed(() => {
      let filtered = isAgendaDataset.value ? scopedDocs.value : scopedDocs.value;

      if (isAgendaDataset.value) {
        return filtered.slice().sort((a, b) => {
          const aPlanned = toDateSafe(a.plannedAt)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          const bPlanned = toDateSafe(b.plannedAt)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          if (aPlanned !== bPlanned) return aPlanned - bPlanned;
          return String(a.email || '').localeCompare(String(b.email || ''));
        });
      }

      return filtered.slice().sort((a, b) => {
        const stateDiff = STATE_META[packageStatus(a)].priority - STATE_META[packageStatus(b)].priority;
        if (stateDiff !== 0) return stateDiff;

        const aExpire = toDateSafe(a.expireAt)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bExpire = toDateSafe(b.expireAt)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (aExpire !== bExpire) return aExpire - bExpire;

        const aCreated = toDateSafe(a.createdAt)?.getTime() ?? 0;
        const bCreated = toDateSafe(b.createdAt)?.getTime() ?? 0;
        return bCreated - aCreated;
      });
    });

    const visibleUserGroups = computed(() => {
      if (isAgendaDataset.value) return [];

      const groups = new Map();
      filteredAndSortedDocs.value.forEach((item) => {
        const groupKey = userGroupKeyForDoc(item);
        if (!groupKey) return;

        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            groupKey,
            uid: item.uid || '',
            email: item.email || '',
            studio: item.studio || '',
            rows: [],
            packageCount: 0,
            totalConsultations: 0,
            hasOperationalHold: false,
            consulenzaStatusCounts: createEmptyConsulenzaStatusCounts(),
            counts: {
              total: 0,
              active: 0,
              expiring: 0,
              expired: 0,
              completed: 0,
            },
          });
        }

        const group = groups.get(groupKey);
        group.rows.push(item);
        if (isOperationallyHidden(item)) group.hasOperationalHold = true;
        group.packageCount += 1;
        group.totalConsultations += Number(item.totalConsultations) || 0;
        group.consulenzaStatusCounts = mergeConsulenzaStatusCounts(
          group.consulenzaStatusCounts,
          item.consulenzaStatusCounts
        );
        group.counts.total += 1;
        group.counts[packageStatus(item)] += 1;
      });

      let result = Array.from(groups.values());
      if (matchingGroupKeys.value) {
        result = result.filter((group) => matchingGroupKeys.value.has(group.groupKey));
      }

      return result;
    });

    const visibleDocs = computed(() => (
      isAgendaDataset.value
        ? filteredAndSortedDocs.value
        : visibleUserGroups.value.flatMap((group) => group.rows)
    ));
    const hasMoreResults = computed(() => false);
    const totalDocsCount = computed(() => visibleDocs.value.length);
    const visibleDocsCount = computed(() => visibleDocs.value.length);
    const visibleUserGroupsCount = computed(() => visibleUserGroups.value.length);

    const availableStudios = computed(() => {
      const values = new Set();
      let hasEmpty = false;

      docsForFilters.value.forEach((item) => {
        if (item.studio) values.add(item.studio);
        else hasEmpty = true;
      });

      const options = Array.from(values)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value }));

      if (hasEmpty) {
        options.push({ value: EMPTY_STUDIO_VALUE, label: 'Nessuno studio' });
      }

      return options;
    });

    const availableConsulenzaStates = computed(() =>
      CONSULENZA_STATUS_OPTIONS.map((status) => ({ value: status, label: status }))
    );

    const availablePackageStates = computed(() =>
      PACKAGE_STATE_OPTIONS.map((state) => ({
        value: state,
        label: STATE_META[state]?.label || state,
      }))
    );

    const availableAgendaConsulenzaStates = computed(() =>
      CONSULENZA_STATUS_OPTIONS.map((status) => ({ value: status, label: status }))
    );

    const availableCategorie = computed(() => {
      const values = new Set();
      docsForFilters.value.forEach((item) => {
        if (item.categoryLabel) values.add(item.categoryLabel);
      });
      return Array.from(values).sort((a, b) => a.localeCompare(b));
    });

    const availableNumeroConsulenze = computed(() => {
      const values = new Set(DEFAULT_NUMERO_CONSULENZE_OPTIONS);
      purchaseSummaries.value.forEach((summary) => {
        const value = Number(summary.totalConsultations);
        if (!Number.isNaN(value) && value > 0) values.add(value);
      });
      return Array.from(values).sort((a, b) => a - b);
    });

    const isUserGroupOpen = (groupKey) => Boolean(openUserGroups[groupKey]);
    const toggleUserGroup = (groupKey) => {
      if (!groupKey) return;
      openUserGroups[groupKey] = !openUserGroups[groupKey];
    };

    watch(selectedDataset, () => {
      selectedStudio.value = '';
      selectedCategoria.value = '';
      selectedNumeroConsulenze.value = '';
      selectedPackageState.value = '';
      selectedConsulenzaState.value = '';
      selectedAgendaConsulenzaState.value = '';
      selectedOperationalHold.value = 'exclude';
      reloadDataset();
    });

    watch([agendaDateFrom, agendaDateTo], () => {
      if (!isAgendaDataset.value) return;
      reloadDataset();
    });

    watch(visibleUserGroups, (groups) => {
      const activeKeys = new Set(groups.map((group) => group.groupKey));
      Object.keys(openUserGroups).forEach((groupKey) => {
        if (!activeKeys.has(groupKey)) {
          delete openUserGroups[groupKey];
        }
      });
    });

    const runBackfill = async () => {
      if (backfillRunning.value) return;
      backfillRunning.value = true;
      backfillStatus.value = 'Aggiornamento indice consulenze in corso…';
      backfillError.value = '';

      try {
        const callable = httpsCallable(functions, 'backfillConsulenzeIndex', {
          timeout: 120000,
        });
        let nextPercorsoId = '';
        let nextLegacyId = '';
        let hasMorePercorsi = true;
        let hasMoreLegacy = true;
        let step = 0;
        let totalPercorsi = 0;
        let totalLegacy = 0;
        let totalPackages = 0;
        let totalAgenda = 0;

        while (hasMorePercorsi || hasMoreLegacy) {
          step += 1;
          if (step > BACKFILL_MAX_LOOPS) {
            throw new Error('Limite di iterazioni superato. Riprova dopo.');
          }

          const payload = { batchSize: BACKFILL_BATCH_SIZE };
          if (nextPercorsoId) payload.startAfterPercorsoId = nextPercorsoId;
          if (nextLegacyId) payload.startAfterLegacyId = nextLegacyId;

          const res = await callable(payload);
          const data = res?.data || {};
          const processedPercorsi = Number(data.processedPercorsi || 0);
          const processedLegacy = Number(data.processedLegacy || 0);
          const packageWritten = Number(data.packageWritten || 0);
          const agendaWritten = Number(data.agendaWritten || 0);

          totalPercorsi += processedPercorsi;
          totalLegacy += processedLegacy;
          totalPackages += packageWritten;
          totalAgenda += agendaWritten;

          hasMorePercorsi = Boolean(data.hasMorePercorsi);
          hasMoreLegacy = Boolean(data.hasMoreLegacy);
          nextPercorsoId = String(data.nextPercorsoId || '');
          nextLegacyId = String(data.nextLegacyId || '');

          backfillStatus.value =
            `Aggiornamento indice: passo ${step} ` +
            `(${totalPercorsi} percorsi, ${totalLegacy} legacy, ` +
            `${totalPackages} pacchetti, ${totalAgenda} agenda)`;

          if (!hasMorePercorsi && !hasMoreLegacy) {
            break;
          }

          if (!hasMorePercorsi && !nextPercorsoId) nextPercorsoId = '';
          if (!hasMoreLegacy && !nextLegacyId) nextLegacyId = '';
          if ((processedPercorsi === 0 && processedLegacy === 0) && step > 1) {
            break;
          }
        }

        if (!hasMorePercorsi && !hasMoreLegacy) {
          backfillStatus.value =
            `Indice aggiornato. Percorsi: ${totalPercorsi}, ` +
            `legacy: ${totalLegacy}, ` +
            `pacchetti: ${totalPackages}, agenda: ${totalAgenda}.`;
        } else {
          backfillStatus.value =
            `Aggiornamento parziale in corso. Percorsi: ${totalPercorsi}, ` +
            `legacy: ${totalLegacy}.`;
        }

        clearDatasetCaches();
        reloadDataset();
      } catch (error) {
        console.error('Errore aggiornando l\'indice consulenze:', error);
        backfillError.value = 'Errore durante l\'aggiornamento dell\'indice.';
        backfillStatus.value = '';
      } finally {
        backfillRunning.value = false;
      }
    };

    const loadMoreResults = () => {};

    const openOrderModal = async (docItem) => {
      if (!docItem?.orderLookupEmail) return;

      activeOrderDocId.value = docItem.rowKey;
      activeOrderEmail.value = docItem.orderLookupEmail;
      showOrderModal.value = true;
      orderLoading.value = true;
      orderError.value = '';
      selectedOrder.value = null;

      try {
        const latest = await fetchLatestOrderByEmail(docItem.orderLookupEmail);
        selectedOrder.value = latest;
        setOrderHighlight(docItem, categorizeOrder(latest));
      } catch (error) {
        console.error('Errore nel recupero dell\'ordine:', error);
        orderError.value = 'Impossibile recuperare gli ordini.';
        selectedOrder.value = null;
        setOrderHighlight(docItem, 'neutral');
      } finally {
        orderLoading.value = false;
      }
    };

    const closeOrderModal = () => {
      showOrderModal.value = false;
      orderError.value = '';
      activeOrderDocId.value = null;
    };

    const orderSummary = computed(() => {
      if (!selectedOrder.value) return null;
      const order = selectedOrder.value;
      const productsArray = Array.isArray(order.products) ? order.products : [];
      const productNames = productsArray
        .map((item) => item?.name || item?.title || item?.productName || item?.sku)
        .filter(Boolean);

      return {
        code: order.orderId || order._id || order.id || '—',
        date: formatOrderDate(order),
        total: formatOrderPrice(order.totalPrice ?? order.total ?? order.amount),
        status: formatStatus(order.status || order.paymentStatus || order.state),
        products: productNames,
      };
    });

    return {
      EXPIRING_THRESHOLD_DAYS,
      isAgendaDataset,
      agendaHasRange,
      selectedDataset,
      selectedStudio,
      selectedCategoria,
      selectedNumeroConsulenze,
      selectedPackageState,
      selectedConsulenzaState,
      agendaDateFrom,
      agendaDateTo,
      availableStudios,
      availablePackageStates,
      availableConsulenzaStates,
      availableAgendaConsulenzaStates,
      availableCategorie,
      availableNumeroConsulenze,
      selectedAgendaConsulenzaState,
      selectedOperationalHold,
      statusCounts,
      consultationStatusTotals,
      visibleDocs,
      visibleDocsCount,
      visibleUserGroups,
      visibleUserGroupsCount,
      isOperationallyHidden,
      totalDocsCount,
      hasMoreResults,
      loadMoreResults,
      isInitialLoading,
      isLoadingMore,
      isFullDataReady,
      isCompletedPackage,
      packageStatus,
      stateLabel,
      sourceLabel,
      openUserConsulenzeInNewTab,
      openUserProgrammiInNewTab,
      isUserGroupOpen,
      toggleUserGroup,
      rowClass,
      isExpiredPackage,
      isExpiryToggleBusy,
      toggleExpired,
      isAgendaStatusBusy,
      isAgendaNoteBusy,
      canEditAgendaStatus,
      canEditAgendaNote,
      updateAgendaConsulenzaStatus,
      updateAgendaConsulenzaNote,
      CONSULENZA_STATUS_OPTIONS,
      CONSULENZA_STATUS_META,
      normalizeConsulenzaStatus,
      consultationStatusBreakdown,
      getConsulenzaStatusCountFromCounts,
      formatDateValue,
      completionLabel,
      expiryHelperLabel,
      orderBadgeStatus,
      openOrderModal,
      closeOrderModal,
      orderLoading,
      orderError,
      orderSummary,
      showOrderModal,
      activeOrderEmail,
      activeOrderDocId,
      productHighlightClass,
      canRunBackfill,
      backfillRunning,
      backfillStatus,
      backfillError,
      runBackfill,
    };
  },
};
</script>

<style scoped>
.titolo-card__info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.titolo-card__subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.titolo-card__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.page-help {
  margin-top: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.page-help__header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 12px;
}

.page-help__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.page-help__subtitle {
  font-size: 0.82rem;
  color: #64748b;
}

.page-help__items {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
}

.page-help__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
}

.page-help__item strong {
  font-size: 0.84rem;
  color: #0f172a;
}

.page-help__item span {
  font-size: 0.82rem;
  line-height: 1.45;
  color: #475569;
}

.page-help__note {
  margin: 12px 0 0;
  font-size: 0.82rem;
  line-height: 1.5;
  color: #475569;
}

.badge-counter,
.threshold-badge {
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
}

.badge-counter {
  background: rgba(33, 99, 214, 0.08);
  color: #1d4ed8;
}

.badge-counter--secondary {
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
}

.threshold-badge {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.backfill-feedback {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 12px 0 4px;
}

.backfill-feedback .error {
  color: #b91c1c;
  font-weight: 600;
}

.dataset-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 14px 0 6px;
  padding: 6px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.dataset-switch__button {
  border: 0;
  background: transparent;
  color: #475569;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.dataset-switch__button--active {
  background: #1d4ed8;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(29, 78, 216, 0.18);
}

.status-cards {
  margin: 16px 0 12px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.consultation-status-cards {
  margin: 0 0 12px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.status-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
  background: #ffffff;
  display: grid;
  gap: 4px;
}

.status-card strong {
  font-size: 1.4rem;
  color: #0f172a;
}

.status-card__label {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.status-card--active {
  background: rgba(37, 99, 235, 0.06);
}

.status-card--expiring {
  background: rgba(245, 158, 11, 0.08);
}

.status-card--expired {
  background: rgba(139, 92, 246, 0.08);
}

.status-card--completed {
  background: rgba(16, 185, 129, 0.08);
}

.consultation-status-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
  background: #ffffff;
  display: grid;
  gap: 4px;
}

.consultation-status-card strong {
  font-size: 1.3rem;
  color: #0f172a;
}

.consultation-status-card--daFare {
  background: rgba(15, 23, 42, 0.05);
}

.consultation-status-card--prenotata {
  background: rgba(14, 165, 233, 0.09);
}

.consultation-status-card--contattata {
  background: rgba(99, 102, 241, 0.1);
}

.consultation-status-card--rifiutata {
  background: rgba(148, 163, 184, 0.16);
}

.consultation-status-card--fatta {
  background: rgba(16, 185, 129, 0.12);
}

.consultation-status-card--scalata {
  background: rgba(217, 119, 6, 0.14);
}

.filters {
  margin: 1.5rem 0 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 0.86rem;
  color: #475569;
  font-weight: 600;
}

.filter-group select {
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid #dbe1ea;
  background: #ffffff;
  color: #0f172a;
}

.filter-group input {
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid #dbe1ea;
  background: #ffffff;
  color: #0f172a;
}

.consulenze-summary {
  font-size: 0.95rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.loading-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(29, 78, 216, 0.08);
  color: #1d4ed8;
  font-size: 0.84rem;
  font-weight: 700;
}

.loading-indicator::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 0 rgba(29, 78, 216, 0.28);
  animation: loadingPulse 1.4s ease-in-out infinite;
}

.consulenze-loader {
  margin: 2rem 0;
  font-weight: 500;
}

.loading-shell {
  margin-top: 18px;
  display: grid;
  gap: 16px;
}

.loading-shell--agenda {
  gap: 14px;
}

.loading-shell__header {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.loading-shell__cards {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.loading-card {
  padding: 16px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  display: grid;
  gap: 12px;
}

.loading-shell__table {
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  overflow: hidden;
  background: #ffffff;
}

.loading-row {
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #edf2f7;
}

.loading-shell--agenda .loading-row {
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

.loading-row--head {
  background: #f8fafc;
}

.loading-row:last-child {
  border-bottom: 0;
}

.skeleton {
  position: relative;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.85) 45%, transparent 100%);
  animation: skeletonShimmer 1.5s ease-in-out infinite;
}

.skeleton--chip {
  width: 122px;
  height: 38px;
}

.skeleton--chip-wide {
  width: 188px;
}

.skeleton--label {
  width: 88px;
  height: 12px;
}

.skeleton--value {
  width: 72%;
  max-width: 180px;
  height: 28px;
  border-radius: 12px;
}

.skeleton--cell {
  width: 100%;
  height: 18px;
  border-radius: 10px;
}

.agenda-empty-state {
  margin: 18px 0;
  padding: 18px 20px;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  background: #f8fafc;
  color: #475569;
  font-weight: 500;
}

.table-wrap {
  margin-top: 12px;
  overflow-x: auto;
}

.table-wrap--nested {
  margin-top: 0;
}

.user-groups {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.user-group {
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  overflow: hidden;
  background: #ffffff;
}

.user-group__header {
  width: 100%;
  border: 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 16px 18px;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  text-align: left;
  cursor: pointer;
}

.user-group__identity {
  display: grid;
  gap: 6px;
}

.user-group__identity strong {
  color: #0f172a;
  font-size: 1rem;
}

.user-group__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #64748b;
  font-size: 0.86rem;
}

.user-group__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.group-stat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.group-stat--consultations {
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
}

.group-stat--active {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
}

.group-stat--expiring {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.group-stat--expired {
  background: rgba(139, 92, 246, 0.16);
  color: #6d28d9;
}

.group-stat--completed {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.group-stat--consulenza-daFare {
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
}

.group-stat--consulenza-prenotata {
  background: rgba(14, 165, 233, 0.12);
  color: #0369a1;
}

.group-stat--consulenza-rifiutata {
  background: rgba(148, 163, 184, 0.18);
  color: #475569;
}

.group-stat--consulenza-fatta {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.group-stat--consulenza-scalata {
  background: rgba(217, 119, 6, 0.14);
  color: #b45309;
}

.user-group__chevron {
  color: #475569;
}

.consulenze-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 60px;
  min-width: 1220px;
}

.consulenze-table--nested {
  margin-bottom: 0;
  min-width: 980px;
}

.consulenze-table th,
.consulenze-table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.consulenze-table th {
  background-color: #f8fafc;
  color: #1f2937;
  font-weight: 700;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.order-cell {
  width: 64px;
}

.action-cell {
  min-width: 250px;
}

.user-cell,
.package-cell,
.count-cell,
.expiry-cell,
.consultation-breakdown {
  display: grid;
  gap: 4px;
}

.user-cell strong,
.package-cell strong,
.count-cell strong,
.expiry-cell strong {
  color: #0f172a;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.muted {
  color: #64748b;
}

.origin-pill,
.state-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  border: 1px solid transparent;
}

.origin-pill--automatiche {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
  border-color: rgba(37, 99, 235, 0.18);
}

.origin-pill--manuali {
  background: rgba(148, 163, 184, 0.16);
  color: #475569;
  border-color: rgba(148, 163, 184, 0.22);
}

.state-pill--active {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
  border-color: rgba(37, 99, 235, 0.18);
}

.state-pill--expiring {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
  border-color: rgba(245, 158, 11, 0.18);
}

.state-pill--expired {
  background: rgba(139, 92, 246, 0.16);
  color: #6d28d9;
  border-color: rgba(139, 92, 246, 0.25);
}

.state-pill--completed {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  border-color: rgba(16, 185, 129, 0.2);
}

.consultation-breakdown {
  min-width: 220px;
}

.consultation-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  border: 1px solid transparent;
}

.consultation-status-pill--daFare {
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
  border-color: rgba(15, 23, 42, 0.1);
}

.consultation-status-pill--prenotata {
  background: rgba(14, 165, 233, 0.12);
  color: #0369a1;
  border-color: rgba(14, 165, 233, 0.18);
}

.consultation-status-pill--contattata {
  background: rgba(99, 102, 241, 0.12);
  color: #4338ca;
  border-color: rgba(99, 102, 241, 0.2);
}

.consultation-status-pill--rifiutata {
  background: rgba(148, 163, 184, 0.18);
  color: #475569;
  border-color: rgba(148, 163, 184, 0.2);
}

.consultation-status-pill--fatta {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  border-color: rgba(16, 185, 129, 0.2);
}

.consultation-status-pill--scalata {
  background: rgba(217, 119, 6, 0.14);
  color: #b45309;
  border-color: rgba(217, 119, 6, 0.22);
}

.agenda-status-select {
  min-width: 125px;
  appearance: none;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  background: #ffffff;
  color: #1f2937;
  padding: 6px 10px;
  font-size: 0.82rem;
  line-height: 1.2;
  font-weight: 600;
}

.agenda-status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.agenda-note-cell {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
  display: grid;
  gap: 4px;
}

.agenda-note-input {
  box-sizing: border-box;
  width: 100%;
  min-height: 42px;
  max-height: 42px;
  resize: none;
  overflow-y: auto;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  background: #ffffff;
  color: #1f2937;
  padding: 8px 10px;
  font-size: 0.84rem;
  line-height: 1.35;
  font-family: inherit;
}

.agenda-note-input:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.expire-toggle-button {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.expire-toggle-button:hover:not(:disabled) {
  border-color: #c4b5fd;
  background: #f5f3ff;
  color: #5b21b6;
}

.expire-toggle-button--active {
  border-color: rgba(139, 92, 246, 0.5);
  background: rgba(139, 92, 246, 0.12);
  color: #6d28d9;
}

.expire-toggle-button:disabled {
  opacity: 0.65;
  cursor: progress;
}

.go-to-user-button {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #d6e2f5;
  background: #ffffff;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #555;
}

.consulenze-pagination {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
}

.order-badge {
  border: 1px solid #d6e2f5;
  background: #ffffff;
  color: #1d4ed8;
  border-radius: 999px;
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.order-badge .material-icons {
  font-size: 20px;
}

.order-badge:hover:not(:disabled) {
  background-color: rgba(33, 99, 214, 0.08);
  box-shadow: 0 2px 8px rgba(33, 99, 214, 0.18);
}

.order-badge:disabled {
  opacity: 0.6;
  cursor: progress;
}

.order-badge--green {
  border-color: rgba(16, 185, 129, 0.4);
  color: #047857;
}

.order-badge--orange {
  border-color: rgba(251, 191, 36, 0.5);
  color: #b45309;
}

.order-badge--neutral {
  color: #1d4ed8;
}

.row--active {
  background: rgba(37, 99, 235, 0.03);
}

.row--expiring {
  background: rgba(245, 158, 11, 0.08);
}

.row--expired {
  background: rgba(139, 92, 246, 0.11);
}

.row--completed {
  background: rgba(16, 185, 129, 0.08);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  pointer-events: none;
}

.modal-backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(1px);
  pointer-events: auto;
}

.modal {
  position: relative;
  pointer-events: auto;
  background: #ffffff;
  border-radius: 14px;
  width: min(380px, calc(100% - 32px));
  max-height: 70vh;
  padding: 18px 18px 16px;
  box-shadow: 0 24px 44px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
}

.modal-header {
  position: relative;
  text-align: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4eaf2;
}

.modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.modal-close {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(0, -50%);
  border: none;
  background: transparent;
  color: #9aa4b4;
  font-size: 1.3rem;
  cursor: pointer;
  line-height: 1;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  color: #111827;
}

.modal-state {
  padding: 18px 4px;
  text-align: center;
  color: #475569;
  font-size: 0.9rem;
}

.modal-state.error {
  color: #b91c1c;
}

.modal-state.muted {
  color: #94a3b8;
}

.order-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-card__body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 14px;
}

.order-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-field__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.order-field__value {
  font-weight: 600;
  color: #1f2937;
}

.order-card__products h4 {
  margin: 0 0 6px;
  font-size: 0.9rem;
  color: #1f2937;
}

.order-products {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.order-product {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
}

.order-product--green {
  background: rgba(16, 185, 129, 0.18);
  color: #047857;
}

.order-product--orange {
  background: rgba(251, 191, 36, 0.22);
  color: #b45309;
}

.order-product--neutral {
  background: rgba(148, 163, 184, 0.22);
  color: #475569;
}

@keyframes skeletonShimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes loadingPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(29, 78, 216, 0.12);
    opacity: 0.75;
  }

  50% {
    box-shadow: 0 0 0 8px rgba(29, 78, 216, 0);
    opacity: 1;
  }
}

@media (max-width: 1180px) {
  .status-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .consultation-status-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .loading-shell__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .loading-row,
  .loading-shell--agenda .loading-row {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .titolo-card__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .filters {
    grid-template-columns: 1fr;
  }

  .status-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .consultation-status-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .loading-shell__cards {
    grid-template-columns: 1fr;
  }

  .user-group__header {
    grid-template-columns: 1fr;
  }

  .user-group__stats {
    justify-content: flex-start;
  }

  .loading-row,
  .loading-shell--agenda .loading-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 14px;
  }
}

@media (max-width: 520px) {
  .status-cards {
    grid-template-columns: 1fr;
  }

  .consultation-status-cards {
    grid-template-columns: 1fr;
  }
}
</style>
